// {{WORKTREE_ROOT}}/picosponsorconsole/packages/design/src/components/TextField.tsx
// PLAN2 WP09 §9.7 (spec table). Canvas: [B-SIGNIN-1440 L1091]. Placeholder is required (C-130): every field shows an
// example. Error is role="alert"; aria-describedby points at the hint or the error (error wins when both are given).
import { forwardRef, useId, type InputHTMLAttributes } from "react";
import { cx } from "../cx.js";
import { DESIGN_TESTIDS as T } from "../testids.js";
import { IconError } from "../icons.js";

export type TextFieldSize = "m" | "l";
const SIZE: Record<TextFieldSize, string> = { l: "p-4 text-15", m: "py-2.5 px-3 text-14" };

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "style" | "size" | "placeholder"> {
  label: string;
  placeholder: string;
  hint?: string;
  error?: string;
  hideLabel?: boolean;
  size?: TextFieldSize;
  mono?: boolean;
  "data-testid"?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, placeholder, hint, error, hideLabel = false, size = "m", mono = false, id, className, "data-testid": testId, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = useId();
  const errorId = useId();
  const describedBy = error ? errorId : hint ? hintId : undefined;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className={hideLabel ? "sr-only" : "text-14 text-dim"}>{label}</label>
      <input
        {...rest}
        ref={ref}
        id={inputId}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        data-testid={testId ?? T.textField.input}
        className={cx(
          "w-full bg-surface text-ink placeholder:text-faint border-1.5 rounded-12",
          error ? "border-red" : "border-control-line",
          SIZE[size],
          mono ? "font-mono" : "",
          className,
        )}
      />
      {!error && hint ? <p id={hintId} className="text-13 text-dim">{hint}</p> : null}
      {error ? (
        <p id={errorId} role="alert" data-testid={T.textField.error} className="flex items-center gap-1.5 text-14 font-bold text-red">
          <IconError aria-hidden="true" className="w-4.5 h-4.5 flex-none" />
          {error}
        </p>
      ) : null}
    </div>
  );
});
